import { MDText } from 'i18n-react';

import type { PricedTransportType, TransportTypes, TransportValue } from 'Types/transportOptions';
import type { Vehicle } from 'Types/vehicle';
import { formatDate, normalizeEmptySpaces } from 'Utilities/formatters';
import { numberWithCommas } from 'Utilities/helpers';

import LocalTexts from '../DeliveryLocation/DeliveryLocation.texts';

const LocalT = new MDText(LocalTexts);

export const TRANSPORT_TYPES: TransportTypes = {
	DRIVEN: 'driven',
	HEAVY_TRANSPORTED: 'heavy-transported',
	TRANSPORTED: 'transported',
};

export const COLLECTION_DATE_FORMAT = 'DD/MM/YY';

const formatCollectionDate = (date: string) => formatDate(date, COLLECTION_DATE_FORMAT);
const translateNamespacedKey = (key: string, translateArgs?: Record<string, unknown>) =>
	LocalT.translate(`available.${key}`, translateArgs);

export const getAvailableRowText = ({
	availableFrom,
	availableUntil,
}: {
	availableFrom?: Vehicle['availableFrom'];
	availableUntil?: Vehicle['availableUntil'];
} = {}): string => {
	const hasAvailableDate = availableFrom || availableUntil;

	if (!hasAvailableDate) {
		return translateNamespacedKey('immediately') as string;
	}

	const hasBothDates = availableFrom && availableUntil;

	const from = availableFrom ? formatCollectionDate(availableFrom) : '';
	const until = availableUntil ? formatCollectionDate(availableUntil) : '';

	const translateArgs = hasBothDates
		? {
				direction: '',
				separator: '-',
		  }
		: {
				direction: translateNamespacedKey(availableFrom ? 'from' : 'until'),
				separator: '',
		  };

	return normalizeEmptySpaces(
		translateNamespacedKey('dates', {
			...translateArgs,
			from,
			until,
		}),
	);
};

const valueMessage = [
	{
		text: 'tooltipContent.driven',
		type: TRANSPORT_TYPES.DRIVEN,
	},
	{
		text: 'tooltipContent.transported',
		type: TRANSPORT_TYPES.TRANSPORTED,
	},
	{
		text: 'tooltipContent.heavyTransported',
		type: TRANSPORT_TYPES.HEAVY_TRANSPORTED,
	},
];

const getCheapestTransport = (value: PricedTransportType[]) =>
	value.reduce((curr, prev) => (curr.price < prev.price ? curr : prev), {
		price: NaN,
		type: TRANSPORT_TYPES.DRIVEN,
	});

const getContent = (transport: PricedTransportType) => valueMessage.find((value) => value.type === transport?.type);

export const getCheapestTransportValue = (transportTypes: PricedTransportType[] = []): TransportValue => {
	const isCheapestTransport = getCheapestTransport(transportTypes);
	const content = getContent(isCheapestTransport);
	const formattedPrice = numberWithCommas(Math.round(isCheapestTransport?.price));

	return {
		content: content?.text,
		price: formattedPrice,
	};
};
